import useBusinessRules from 'hooks/useBusinessRules';
import { FC, useCallback, useEffect, useMemo } from 'react';
import { SubmissionErrors } from 'store/declarations/ireland/ireland-notification';
import AisBusinessRuleRow from 'views/documentation/ais-documentation/AisBusinessRuleRow';
import {
    DetailsLabel,
    DetailsLabelDiv,
    DetailsValue,
    DetailsValueDiv,
    StyledErrorRow,
    StyledHr,
    Title,
} from '../../DeclarationsNotificationsDrawer.styles';
import ListErrorsRows from '../ListErrorsRows';
import useGetDeclarationMapValues from 'hooks/useGetDeclarationMapValues';
import { useFormikContext } from 'formik';
import { ResolvedErrorField } from '../resolveErrorField';
import useGoToErrorField from '../useGoToErrorField';
import useErrorCardContext from '../error-cards/useErrorCardContext';
import formatErrorField from '../error-cards/formatErrorField';
import getAisErrorCards from './getAisErrorCards';

export const ErrorRow: FC<{ label: string; value?: string | number }> = ({ label, value }) => {
    return (
        <>
            <StyledErrorRow>
                <DetailsLabelDiv>
                    <DetailsLabel>{label}</DetailsLabel>
                </DetailsLabelDiv>
                <DetailsValueDiv>
                    <DetailsValue>{`${value ?? '-'}`}</DetailsValue>
                </DetailsValueDiv>
            </StyledErrorRow>
        </>
    );
};

export const RevenueImportErrorDetails: FC<{
    details: SubmissionErrors | undefined;
    onClose?: () => void;
}> = ({ details, onClose }) => {
    const { context } = useErrorCardContext();
    const goToErrorField = useGoToErrorField({ onClose, box44Paths: context?.box44Paths });
    const { aisRules, getIrelandBusinessRules } = useBusinessRules();
    const formikContext = useFormikContext();
    // H1, H7 and I1 all arrive here and name their items differently, so the item path and the
    // parser come from the declaration rather than being fixed to H1's.
    const { parseForm, itemPath } = useGetDeclarationMapValues();

    useEffect(() => {
        if (!aisRules) {
            getIrelandBusinessRules('AIS');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const businessRule = useMemo(() => {
        const r = aisRules?.find((p) => p.code === details?.businessRuleCode);

        if (!r) return <></>;

        return (
            <>
                <StyledHr></StyledHr>
                <Title>Revenue business rule</Title>
                <AisBusinessRuleRow rule={r} />
                <span>
                    <a
                        href="https://www.revenue.ie/en/online-services/support/software-developers/documents/ais/ais-business-rules-and-conditions.pdf"
                        rel="noreferrer"
                        target="_blank"
                    >
                        AIS Business Rules and Conditions
                    </a>
                </span>
            </>
        );
    }, [details, aisRules]);

    // The fields as the error's card resolves them - labelled and opened the same way, with the item
    // found by id and named by its number today.
    const cardFieldByPath = useMemo(() => {
        const cardFields = context && details ? getAisErrorCards([details], context)[0].fields : [];
        return new Map(cardFields.map((field, index) => [details?.fields[index], field]));
    }, [context, details]);

    const getFieldLabel = useCallback(
        (path: string) => {
            const cardField = cardFieldByPath.get(path);
            return cardField && formatErrorField(cardField);
        },
        [cardFieldByPath]
    );

    const handleOnClickField = (path: string) => {
        if (!path) return;

        const resolved: ResolvedErrorField = cardFieldByPath.get(path) ?? { formPath: path };
        goToErrorField(resolved);

        // An item deleted since the rejection has no form to mark the field on.
        const isDeletedItem = resolved.item && !resolved.item.itemNumberToday;
        if (isDeletedItem) return;

        formikContext?.getFieldHelpers(resolved.formPath).setError(details?.irelandTranslationTable?.message ?? '-');
        formikContext?.setFieldTouched(resolved.formPath, true, false);
    };

    return (
        <>
            <Title>Error details</Title>
            <ErrorRow label="Code:" value={details?.irelandTranslationTable?.code ?? '-'} />
            <ErrorRow label="Value:" value={details?.value ?? '-'} />
            <ListErrorsRows
                section="Fields"
                fields={details?.fields ?? []}
                handleOnClickField={handleOnClickField}
                parseDeclaration={parseForm}
                itemsPath={itemPath}
                getLabel={getFieldLabel}
            />
            <ErrorRow label="Message:" value={details?.irelandTranslationTable?.message ?? '-'} />
            <ErrorRow label="Error Message:" value={details?.message ?? '-'} />
            <ErrorRow label="Remarks:" value={details?.remarks ?? '-'} />
            <ErrorRow label="Date:" value={details?.date ?? '-'} />
            <ErrorRow label="Pattern Required:" value={details?.patternRequired ?? '-'} />
            <ErrorRow label="Error Reason:" value={details?.errorReason ?? '-'} />
            <ErrorRow label="External Field:" value={details?.externalField ?? '-'} />
            <ErrorRow label="External Error Message:" value={details?.externalErrorMessage ?? '-'} />
            <ListErrorsRows
                section="Missing Documents:"
                fields={details?.missingDocuments ?? []}
                parseDeclaration={parseForm}
                itemsPath={itemPath}
            />
            {details?.businessRuleCode ? businessRule : <></>}
        </>
    );
};
